"use client";

import React from "react";
import Image from "next/image";
import { ArrowRight, TrendingUp } from "lucide-react";
import { LANDING_STATS } from "@/lib/landing-constants";
import { ALL_TESTIMONIALS, type Testimonial } from "@/lib/testimonial-data";
import { useReveal } from "@/lib/use-reveal";
import { trackMarketingCtaClick } from "@/utils/analytics";

const ROW_HIGH = ALL_TESTIMONIALS.filter((t) => t.tier === "high");
const ROW_MID = ALL_TESTIMONIALS.filter((t) => t.tier === "mid");
const ROW_STANDARD = ALL_TESTIMONIALS.filter((t) => t.tier === "standard");

const MOBILE_LANDSCAPE_EXCLUDE = new Set([8, 9, 12, 13, 22]);
const isMobileFriendly = (t: Testimonial) => !MOBILE_LANDSCAPE_EXCLUDE.has(t.id);
const MOBILE_ROW_1 = ROW_HIGH.filter(isMobileFriendly);
const MOBILE_ROW_2 = ROW_MID.filter(isMobileFriendly).slice(0, 8);

const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
  const img = event.currentTarget;
  if (img.dataset.fallbackApplied === "true") return;
  img.dataset.fallbackApplied = "true";
  img.src = "/images/sportsbooks/upside_logo.png";
};

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  const isHigh = testimonial.tier === "high";
  const isMid = testimonial.tier === "mid";

  return (
    <div className={`flex-shrink-0 ${isHigh ? "w-[340px]" : "w-[300px]"}`}>
      <div
        className={`rounded-2xl overflow-hidden border bg-card shadow-sm ${
          isHigh ? "border-success/30 ring-1 ring-success/20" : "border-border"
        }`}
      >
        {testimonial.imageSrc && (
          <div
            className="relative overflow-hidden"
            style={{ aspectRatio: testimonial.aspectRatio }}
          >
            <Image
              src={testimonial.imageSrc}
              alt={`${testimonial.name}'s winning bet`}
              fill
              sizes={isHigh ? "340px" : "300px"}
              className="object-cover object-top"
              onError={handleImageError}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />

            {testimonial.profit && (
              <div className={`absolute top-3 right-3 ${isHigh ? "scale-110" : ""}`}>
                <div
                  className={`backdrop-blur-sm rounded-full flex items-center border bg-zinc-950/85 ${
                    isHigh
                      ? "px-3 py-1.5 gap-1.5 border-success/30"
                      : isMid
                        ? "px-2.5 py-1 gap-1 border-success/20"
                        : "px-2 py-1 gap-1 border-border/30"
                  }`}
                >
                  <TrendingUp
                    className={`text-success ${isHigh ? "size-4" : "w-3 h-3"}`}
                  />
                  <span
                    className={`font-bold text-success data-num ${
                      isHigh ? "text-sm" : "text-xs"
                    }`}
                  >
                    {testimonial.profit}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="p-4">
          <p className="text-xs text-foreground/90 leading-relaxed mb-3 line-clamp-3">
            {testimonial.text}
          </p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Image
                  src={testimonial.avatarSrc}
                  alt={testimonial.name}
                  width={28}
                  height={28}
                  className="rounded-full ring-2 ring-border"
                  onError={handleImageError}
                />
                <div className="absolute -bottom-0.5 -right-0.5 size-3.5 bg-[#1DA1F2] rounded-full flex items-center justify-center">
                  <svg
                    className="size-2 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M13.7 10.6L20.2 3H18.7L13 9.6L8.4 3H3.2L10.1 13L3.2 21H4.8L10.8 14L15.6 21H20.8L13.7 10.6H13.7ZM11.6 13.1L10.9 12.1L5.3 4.2H7.7L12.2 10.6L12.9 11.6L18.7 19.9H16.3L11.6 13.1V13.1Z" />
                  </svg>
                </div>
              </div>
              <div>
                <p className="font-semibold text-xs text-foreground">
                  {testimonial.name}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {testimonial.handle}
                  {testimonial.postedAt ? ` · ${testimonial.postedAt}` : ""}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileMarqueeCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <div className="flex-shrink-0 w-[170px]">
      <div className="rounded-xl overflow-hidden border border-border/60 bg-card">
        {testimonial.imageSrc && (
          <div className="relative overflow-hidden" style={{ aspectRatio: "1/1" }}>
            <Image
              src={testimonial.imageSrc}
              alt={`${testimonial.name}'s winning bet`}
              fill
              sizes="170px"
              className="object-cover object-top"
              onError={handleImageError}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-card/80 via-transparent to-transparent" />

            {testimonial.profit && (
              <div className="absolute top-1.5 right-1.5">
                <div className="backdrop-blur-sm rounded-full flex items-center gap-0.5 bg-zinc-950/85 border border-success/30 px-1.5 py-0.5">
                  <TrendingUp className="size-2.5 text-success" />
                  <span className="text-[10px] font-bold text-success data-num">
                    {testimonial.profit}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="px-2 pt-2 pb-1.5">
          <p className="text-[10px] text-foreground/90 leading-relaxed mb-1.5 line-clamp-2">
            {testimonial.text}
          </p>
          <div className="flex items-center gap-1.5">
            <Image
              src={testimonial.avatarSrc}
              alt={testimonial.name}
              width={18}
              height={18}
              className="size-[18px] flex-shrink-0 rounded-full ring-1 ring-border"
              onError={handleImageError}
            />
            <div className="min-w-0">
              <p className="font-semibold text-[10px] text-foreground truncate">
                {testimonial.name}
              </p>
              <p className="text-[9px] text-muted-foreground truncate">
                {testimonial.handle}
                {testimonial.postedAt ? ` · ${testimonial.postedAt}` : ""}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileMarqueeRow({
  testimonials,
  direction,
}: {
  testimonials: Testimonial[];
  direction: "left" | "right";
}) {
  const extended = [...testimonials, ...testimonials];
  const animationName = direction === "left" ? "marquee-left" : "marquee-right";
  const duration = Math.max(36, (testimonials.length * 185) / MOBILE_MARQUEE_SPEED);

  return (
    <div className="overflow-hidden">
      <div
        className="flex w-max items-start gap-2.5 marquee-track"
        style={{
          animationName,
          animationDuration: `${duration}s`,
          animationTimingFunction: "linear",
          animationIterationCount: "infinite",
        }}
      >
        {extended.map((testimonial, index) => (
          <MobileMarqueeCard
            key={`m-${testimonial.id}-${index}`}
            testimonial={testimonial}
          />
        ))}
      </div>
    </div>
  );
}

const DESKTOP_MARQUEE_SPEED = 50;
const MOBILE_MARQUEE_SPEED = 40;

function MarqueeRow({
  testimonials,
  direction,
}: {
  testimonials: Testimonial[];
  direction: "left" | "right";
}) {
  const extended = [...testimonials, ...testimonials];
  const animationName = direction === "left" ? "marquee-left" : "marquee-right";
  const averageCardWidth = testimonials.some((testimonial) => testimonial.tier === "high")
    ? 356
    : 316;
  const duration = Math.max(
    48,
    (testimonials.length * averageCardWidth) / DESKTOP_MARQUEE_SPEED,
  );

  return (
    <div className="overflow-hidden">
      <div
        className="flex w-max items-start gap-4 marquee-track"
        style={{
          animationName,
          animationDuration: `${duration}s`,
          animationTimingFunction: "linear",
          animationIterationCount: "infinite",
        }}
      >
        {extended.map((testimonial, index) => (
          <TestimonialCard
            key={`${testimonial.id}-${index}`}
            testimonial={testimonial}
          />
        ))}
      </div>
    </div>
  );
}

export function SocialProofSection() {
  const [sectionRef, isInView] = useReveal<HTMLElement>();
  const shouldRenderMarquee = isInView;

  return (
    <section ref={sectionRef} className="py-20 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 mb-12">
        <div data-revealed={isInView} className="reveal text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-[-0.035em] text-balance mb-4">
            <span className="text-foreground">
              The Profits Speak for Themselves
            </span>
          </h2>
        </div>
      </div>

      <div className="min-h-[520px] md:min-h-[760px]">
        {shouldRenderMarquee && (
          <>
            <div className="hidden md:block marquee-fade">
              <div className="space-y-4 pb-4">
                <MarqueeRow
                  testimonials={ROW_HIGH}
                  direction="left"
                />
                <MarqueeRow
                  testimonials={ROW_MID}
                  direction="right"
                />
                <MarqueeRow
                  testimonials={ROW_STANDARD}
                  direction="left"
                />
              </div>
            </div>

            <div className="md:hidden marquee-fade-mobile">
              <div className="space-y-2.5 pb-4">
                <MobileMarqueeRow
                  testimonials={MOBILE_ROW_1}
                  direction="left"
                />
                <MobileMarqueeRow
                  testimonials={MOBILE_ROW_2}
                  direction="right"
                />
              </div>
            </div>
          </>
        )}

        {!shouldRenderMarquee && (
          <div className="marquee-fade hidden md:block" aria-hidden="true">
            <div className="space-y-4 pb-4">
              {[0, 1, 2].map((row) => (
                <div key={row} className="flex gap-4">
                  {[0, 1, 2, 3].map((item) => (
                    <div
                      key={item}
                      className="h-56 w-[300px] flex-shrink-0 rounded-2xl border border-border bg-card/60"
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {!shouldRenderMarquee && (
          <div className="marquee-fade-mobile md:hidden" aria-hidden="true">
            <div className="space-y-2.5 pb-4">
              {[0, 1].map((row) => (
                <div key={row} className="flex gap-2.5">
                  {[0, 1, 2].map((item) => (
                    <div
                      key={item}
                      className="h-64 w-[170px] flex-shrink-0 rounded-xl border border-border/60 bg-card/60"
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          data-revealed={isInView}
          style={{ transitionDelay: "300ms" }}
          className="reveal max-w-4xl mx-auto mt-12 px-4"
        >
          <div className="rounded-2xl p-6 border border-border bg-card shadow-sm surface-toplight">
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-2xl md:text-3xl font-bold text-foreground data-num">
                  {LANDING_STATS.ACTIVE_USERS_DISPLAY}
                </div>
                <div className="text-sm text-muted-foreground">Active Users</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-foreground data-num">
                  {LANDING_STATS.SPORTSBOOKS_SCANNED_DISPLAY}
                </div>
                <div className="text-sm text-muted-foreground">
                  Sportsbooks Scanned
                </div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-foreground data-num">
                  {LANDING_STATS.ODDS_REFRESH_DISPLAY}
                </div>
                <div className="text-sm text-muted-foreground">Odds Refresh</div>
              </div>
            </div>
            <div className="mt-6 flex justify-center border-t border-border pt-5">
              <a
                href="#pricing"
                data-plan-chooser="home_social_proof"
                onClick={() =>
                  trackMarketingCtaClick({
                    surface: "home_social_proof",
                    cta: "start_trial",
                    destination: "#pricing",
                  })
                }
                className="group btn-toplight inline-flex items-center gap-2 rounded-lg bg-accent-value px-5 py-2.5 text-sm font-semibold text-zinc-950 transition-colors hover:bg-accent-value-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
              >
                Start my 5-day free trial
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
